import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Star, FileText, User, Calendar, Clock, Award } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface InterviewSchedule {
  id: string;
  job_application_id: string;
  business_id: string;
  interview_type: string;
  scheduled_at: string;
  duration_minutes: number;
  location?: string;
  meeting_link?: string;
  notes?: string;
  status: string;
  evaluation_notes?: string;
  technical_score?: number;
  communication_score?: number;
  culture_fit_score?: number;
  overall_recommendation?: string;
}

interface JobApplication {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  status: string;
}

interface InterviewEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  interview: InterviewSchedule | null;
  application: JobApplication | null;
  onEvaluationSaved?: () => void;
}

const InterviewEvaluationModal: React.FC<InterviewEvaluationModalProps> = ({
  isOpen,
  onClose,
  interview,
  application,
  onEvaluationSaved
}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    evaluation_notes: '',
    technical_score: 70,
    communication_score: 70,
    culture_fit_score: 70,
    overall_recommendation: 'hire',
    status: 'completed'
  });

  useEffect(() => {
    if (isOpen && interview) {
      setFormData({
        evaluation_notes: interview.evaluation_notes || '',
        technical_score: interview.technical_score || 70,
        communication_score: interview.communication_score || 70,
        culture_fit_score: interview.culture_fit_score || 70,
        overall_recommendation: interview.overall_recommendation || 'hire',
        status: interview.status === 'completed' ? 'completed' : 'completed'
      });
    }
  }, [isOpen, interview]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!interview || !application) return;

    if (!formData.evaluation_notes.trim()) {
      toast.error('Please provide evaluation notes');
      return;
    }

    setSaving(true);

    try {
      // Calculate overall interview score based on the three categories
      const overallScore = Math.round(
        (formData.technical_score + formData.communication_score + formData.culture_fit_score) / 3
      );

      // Update interview with evaluation
      const { error: interviewError } = await supabase
        .from('interview_schedules')
        .update({
          evaluation_notes: formData.evaluation_notes.trim(),
          technical_score: formData.technical_score,
          communication_score: formData.communication_score,
          culture_fit_score: formData.culture_fit_score,
          overall_recommendation: formData.overall_recommendation,
          status: 'completed'
        })
        .eq('id', interview.id);

      if (interviewError) throw interviewError;

      // Update job application with interview score and status
      const { error: applicationError } = await supabase
        .from('job_applications')
        .update({
          interview_score: overallScore,
          status: 'interviewed',
          interviewed_at: new Date().toISOString()
        })
        .eq('id', application.id);

      if (applicationError) throw applicationError;

      toast.success('Interview evaluation saved successfully');
      onEvaluationSaved?.();
      onClose();
    } catch (error: unknown) {
      console.error('Error saving evaluation:', error);
      toast.error('Failed to save evaluation');
    } finally {
      setSaving(false);
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'strong-hire': return 'bg-green-600 text-white';
      case 'hire': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'no-hire': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'strong-no-hire': return 'bg-red-600 text-white';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!interview || !application) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Interview Evaluation
          </DialogTitle>
          <DialogDescription>
            Evaluate the interview for {application.first_name} {application.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Interview Details */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Interview Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{application.first_name} {application.last_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="capitalize">{interview.interview_type.replace('-', ' ')} Interview</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{format(parseISO(interview.scheduled_at), 'PPP')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{format(parseISO(interview.scheduled_at), 'p')} ({interview.duration_minutes} min)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Evaluation Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Scoring Categories */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Evaluation Scores
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Technical Score */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="technical_score">Technical Skills</Label>
                    <span className={`text-lg font-bold ${getScoreColor(formData.technical_score)}`}>
                      {formData.technical_score}/100
                    </span>
                  </div>
                  <Slider
                    id="technical_score"
                    min={0}
                    max={100}
                    step={5}
                    value={[formData.technical_score]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, technical_score: value[0] }))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Rate the candidate's technical competency and job-specific skills
                  </p>
                </div>

                {/* Communication Score */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="communication_score">Communication</Label>
                    <span className={`text-lg font-bold ${getScoreColor(formData.communication_score)}`}>
                      {formData.communication_score}/100
                    </span>
                  </div>
                  <Slider
                    id="communication_score"
                    min={0}
                    max={100}
                    step={5}
                    value={[formData.communication_score]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, communication_score: value[0] }))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Evaluate clarity of communication, listening skills, and articulation
                  </p>
                </div>

                {/* Culture Fit Score */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="culture_fit_score">Culture Fit</Label>
                    <span className={`text-lg font-bold ${getScoreColor(formData.culture_fit_score)}`}>
                      {formData.culture_fit_score}/100
                    </span>
                  </div>
                  <Slider
                    id="culture_fit_score"
                    min={0}
                    max={100}
                    step={5}
                    value={[formData.culture_fit_score]}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, culture_fit_score: value[0] }))}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Assess alignment with company values and team dynamics
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Overall Recommendation */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Overall Recommendation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="overall_recommendation">Hiring Recommendation</Label>
                  <Select 
                    value={formData.overall_recommendation} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, overall_recommendation: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strong-hire">Strong Hire</SelectItem>
                      <SelectItem value="hire">Hire</SelectItem>
                      <SelectItem value="no-hire">No Hire</SelectItem>
                      <SelectItem value="strong-no-hire">Strong No Hire</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-2">
                    <Badge className={getRecommendationColor(formData.overall_recommendation)}>
                      {formData.overall_recommendation.replace('-', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label htmlFor="evaluation_notes">Evaluation Notes *</Label>
                  <Textarea
                    id="evaluation_notes"
                    value={formData.evaluation_notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, evaluation_notes: e.target.value }))}
                    placeholder="Provide detailed feedback about the candidate's performance, strengths, areas for improvement, and rationale for your recommendation..."
                    rows={6}
                    required
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                <CardTitle className="text-sm font-medium">Evaluation Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center space-y-2">
                  <div className="text-3xl font-bold text-primary">
                    {Math.round((formData.technical_score + formData.communication_score + formData.culture_fit_score) / 3)}/100
                  </div>
                  <p className="text-muted-foreground">Overall Interview Score</p>
                  <Badge className={getRecommendationColor(formData.overall_recommendation)} size="lg">
                    {formData.overall_recommendation.replace('-', ' ').toUpperCase()}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>

        <DialogFooter className="pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Evaluation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewEvaluationModal;